<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { Button } from "@/components/ui/button";
import FenixLogo from "./FenixLogo.vue";
import ThemeToggle from "./ThemeToggle.vue";
import { Menu, X } from "lucide-vue-next";

const { t } = useI18n();

const navLinks = computed(() => [
  { label: t("landing.header.nav.features"), href: "#features" },
  { label: t("landing.header.nav.pricing"), href: "#pricing" },
  { label: t("landing.header.nav.contact"), href: "#contact-form" },
]);

const isScrolled = ref(false);
const isMobileMenuOpen = ref(false);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<template>
  <header 
    :class="[
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled 
        ? 'bg-background/95 backdrop-blur-md border-b border-border/30' 
        : 'bg-transparent'
    ]"
  >
    <div class="container mx-auto px-6">
      <div class="flex items-center justify-between h-24 lg:h-32">
        <!-- Logo -->
        <FenixLogo size="2xl" />

        <!-- Desktop Navigation -->
        <nav class="hidden md:flex items-center gap-8">
          <a
            v-for="link in navLinks"
            :key="link.href"
            :href="link.href"
            class="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {{ link.label }}
          </a>
        </nav>

        <!-- Desktop CTA -->
        <div class="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <router-link to="/login">
            <Button variant="ghost" size="sm">
              {{ t("landing.header.cta.login") }}
            </Button>
          </router-link>
          <router-link to="/register">
            <Button variant="cyan-outline" size="sm">
              {{ t("landing.header.cta.start_free") }}
            </Button>
          </router-link>
        </div>

        <!-- Mobile Menu Button -->
        <button
          class="md:hidden p-2 text-foreground"
          @click="isMobileMenuOpen = !isMobileMenuOpen"
          :aria-label="t('landing.header.toggle_menu')"
        >
          <X v-if="isMobileMenuOpen" class="w-6 h-6" />
          <Menu v-else class="w-6 h-6" />
        </button>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div v-if="isMobileMenuOpen" class="md:hidden bg-background/98 backdrop-blur-md border-t border-border/30">
      <div class="container mx-auto px-6 py-6">
        <nav class="flex flex-col gap-4 mb-6">
          <a
            v-for="link in navLinks"
            :key="link.href"
            :href="link.href"
            class="text-base text-muted-foreground hover:text-foreground transition-colors py-2"
            @click="isMobileMenuOpen = false"
          >
            {{ link.label }}
          </a>
        </nav>
        <div class="flex flex-col gap-3">
          <div class="flex justify-center mb-2">
            <ThemeToggle />
          </div>
          <router-link to="/login" @click="isMobileMenuOpen = false">
            <Button variant="ghost" class="w-full justify-center">
              {{ t("landing.header.cta.login") }}
            </Button>
          </router-link>
          <router-link to="/register" @click="isMobileMenuOpen = false">
            <Button variant="cyan-outline" class="w-full justify-center">
              {{ t("landing.header.cta.start_free") }}
            </Button>
          </router-link>
        </div>
      </div>
    </div>
  </header>
</template>
